import { Injectable } from '@angular/core'
import { mergeMap, Observable, Subject, throwError } from 'rxjs'
import { filter, map, retryWhen } from 'rxjs/operators'

import { ApiService } from '@shared/services/api.service'
import {
  Chat,
  ChatFormData,
  ChatListFilterEnum,
  ChatMessage,
  ChatMessageFormData,
  ChatMessageModel,
  ChatModel,
  ChatUser,
  ChatUserModel,
  UserChannelsResponse,
} from '@shared/models/messenger/messenger.model'
import { Meta, PaginationData } from '@shared/models/pages-data.model'
import { UserInList } from '@shared/models/user.model'
import { toFormData } from '@core/helpers/to-form-data'
import { DialogService } from '@shared/dialogs/dialog.service'
import { HttpErrorResponse } from '@angular/common/http'
import { TranslateService } from '@ngx-translate/core'

@Injectable({
  providedIn: 'root',
})
export class MessengerApiService {
  private readonly defaultOptions = { responseType: 'json' }

  constructor(
    private readonly apiService: ApiService,
    private readonly dialogService: DialogService,
    private readonly translateService: TranslateService,
  ) {}

  getChatList(params?: Partial<{ scope: ChatListFilterEnum; isUnread?: number } & Meta>): Observable<PaginationData<ChatModel[]>> {
    const options = {
      ...this.defaultOptions,
      params: { limit: 20, ...(params && params) },
    }

    const populateResponse = (response: PaginationData<ChatModel[]>): PaginationData<ChatModel[]> => ({
      ...response,
      data: response.data.map(this.populateChatResponseData),
    })

    return this.apiService.get<PaginationData<ChatModel[]>>('/chats', options).pipe(map(populateResponse))
  }

  getChatById(chatId: number): Observable<ChatModel> {
    return this.apiService
      .get<ChatModel>(`/chats/${chatId}`, this.defaultOptions)
      .pipe(this.handleError<ChatModel>(), map(this.populateChatResponseData))
  }

  getUserChannels(): Observable<UserChannelsResponse> {
    return this.apiService.get<UserChannelsResponse>('/chats/user-channels', this.defaultOptions)
  }

  createChat(body: ChatFormData): Observable<ChatModel> {
    return this.apiService.post<ChatModel>('/chats', body, this.defaultOptions)
  }

  getClientUsers(clientId: number, additionalFields?: string[]): Observable<UserInList[]> {
    let fields = ['id', 'name', 'surname', 'team', 'fullName']
    additionalFields?.length && (fields = fields.concat(additionalFields))

    const params = {
      page: 0,
      limit: 1000,
      'fields[]': fields,
    }

    return this.apiService
      .get<PaginationData<UserInList[]>>(`/clients/${clientId}/users`, { ...this.defaultOptions, params })
      .pipe(map(({ data }) => data.map((user) => new UserInList(user))))
  }

  getMessageList(chatId: number, params?: Partial<Meta>): Observable<PaginationData<ChatMessageModel[]>> {
    const options = {
      ...this.defaultOptions,
      params: { sort: '-createdAt', limit: 20, ...(params && params) },
    }

    const populateResponseData = (data: ChatMessageModel): ChatMessageModel => new ChatMessage(data)

    const populateResponse = (response: PaginationData<ChatMessageModel[]>): PaginationData<ChatMessageModel[]> => ({
      ...response,
      data: response.data.reverse().map(populateResponseData),
    })

    return this.apiService.get<PaginationData<ChatMessageModel[]>>(`/chats/${chatId}/history`, options).pipe(map(populateResponse))
  }

  postMessage(chatId: number, body: ChatMessageFormData): Observable<ChatMessageModel> {
    return this.apiService
      .post<ChatMessageModel>(`/chats/${chatId}/message`, toFormData(body), { responseType: 'json' })
      .pipe(this.handleError<ChatMessageModel>())
  }

  postUserOnline(): Observable<ChatUserModel | null> {
    // No caching: chat-toolbar / the v2 shell ping /users/online every 60s to keep the user online,
    // so each call must issue a fresh POST. A shareReplay(1) cache made it fire only once per session.
    // 2.0 answers with the user (SIMPLE_VALUES_CHAT); 3.0 answers 204 No Content (presence comes
    // from the userStatusUpdated socket event). Building a ChatUser from the empty body threw and
    // killed the heartbeat stream after the first ping (L30-1505), so an empty body maps to null.
    return this.apiService
      .post<ChatUserModel | null>(`/users/online`, undefined, this.defaultOptions)
      .pipe(map((response) => (response ? new ChatUser(response) : null)))
  }

  patchReadMessage(chatId: number, messageId: number, body?: ChatMessageModel): Observable<ChatMessageModel> {
    return this.apiService.patch<ChatMessageModel>(
      `/chats/${chatId}/message/${messageId}`,
      {
        ...(body && body),
        isRead: true,
      },
      { responseType: 'json' },
    )
  }

  /* Drops CORS in local */
  getFileSource(url: string): Observable<Blob> {
    return this.apiService.get<Blob>(url, { responseType: 'blob' }, true)
  }

  addRemoveUserToChat(chatId: number, body: ChatFormData): Observable<ChatModel> {
    return this.apiService.post<ChatModel>(`/chats/${chatId}/users`, body, this.defaultOptions)
  }

  editChatTitle(chatId: number, body: Pick<ChatModel, 'name'>): Observable<ChatModel> {
    return this.apiService.patch<ChatModel>(`/chats/${chatId}`, body, { responseType: 'json' })
  }

  editChat(chatId: number, body: Pick<ChatModel, 'name'>): Observable<ChatModel> {
    return this.apiService.patch<ChatModel>(`/chats/${chatId}`, body, this.defaultOptions)
  }

  editChatMessage(chatId: number | string, messageId: number | string, body: { message: string }): Observable<ChatMessageModel> {
    return this.apiService
      .patch<ChatMessageModel>(`/chats/${chatId}/message/${messageId}`, body, { responseType: 'json' })
      .pipe(this.handleError<ChatMessageModel>())
  }

  deleteChatMessage(chatId: number | string, messageId: number | string): Observable<ChatMessageModel> {
    return this.apiService.delete<ChatMessageModel>(`/chats/${chatId}/message/${messageId}`).pipe(this.handleError<ChatMessageModel>())
  }

  deleteAttachment(chatId: number | string, messageId: number | string, attachmentId: number | string): Observable<ChatMessageModel> {
    return this.apiService
      .delete<ChatMessageModel>(`/chats/${chatId}/message/${messageId}/file/${attachmentId}`)
      .pipe(this.handleError<ChatMessageModel>())
  }

  private populateChatResponseData = (chat: ChatModel): ChatModel => new Chat(chat)

  private handleError<T>(): (source: Observable<T>) => Observable<T> {
    const mergeRequestWithModal = (errorResponse: HttpErrorResponse): Observable<void | unknown> => {
      if (errorResponse.status >= 500) {
        const closedSubject = new Subject<void>()
        const errorTitle = <string>this.translateService.instant('MAIN.ERROR_PAGE_TITLE')
        const errorMessage = <string>this.translateService.instant('MAIN.ERROR_PAGE_TITLE_RETRY')

        this.dialogService
          .retry(errorMessage, errorTitle)
          .afterClosed()
          .pipe(filter(Boolean))
          .subscribe(() => {
            closedSubject.next()
          })

        return closedSubject
      }
      return throwError(() => new Error(errorResponse.message))
    }

    const errorRetry = (errors: Observable<Error>): Observable<void | unknown> => errors.pipe(mergeMap(mergeRequestWithModal))

    return (source: Observable<T>): Observable<T> => source.pipe(retryWhen(errorRetry))
  }
}
